import { useState } from "react";

import { LOGIN_PATH, SIGNUP_UNAVAILABLE_MESSAGE } from "@buildpy/core/modules/onboarding/rules";
import Banner from "@buildpy/ui/banner";
import SectionCard from "@buildpy/ui/section-card";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import { Link } from "@/lib/nav";
import SignupForm from "@/modules/onboarding/ui/signup-form";
import SignupSent from "@/modules/onboarding/ui/signup-sent";
import SignupSteps from "@/modules/onboarding/ui/signup-steps";
import { PRIVACY_PATH, TERMS_PATH } from "@/modules/plan/copy";

export interface SentState {
  email: string;
}

const LINK = "underline underline-offset-4 hover:text-foreground";

interface ViewProps {
  sent: SentState | null;
  onSent: (next: SentState) => void;
  onReset: () => void;
  unavailable?: boolean;
  onUnavailable?: () => void;
}

export const SignupView = ({ sent, onSent, onReset, unavailable = false, onUnavailable }: ViewProps) => (
  <main className="safe-top-fill flex min-h-screen items-center justify-center bg-background px-4 pb-4 pt-safe-4">
    <SectionCard as="div" className="flex w-full max-w-md flex-col gap-y-6" data-testid="signup-page" padding="lg">
      <div className="flex flex-col gap-y-3">
        <Link aria-label="buildpy 첫 화면" className="flex" href="/">
          <BrandLogo size="lg" />
        </Link>
        <SignupSteps current={sent ? "메일 확인" : "계정"} />
      </div>
      {sent ? (
        <SignupSent email={sent.email} onChangeEmail={onReset} />
      ) : (
        <>
          <div className="flex flex-col gap-y-1">
            <h1 className="text-xl font-bold">무료로 시작하기</h1>
            <p className="text-sm text-muted-foreground">회사 정보는 메일 확인 뒤에 받습니다.</p>
          </div>
          {unavailable ? (
            <div className="flex flex-col gap-y-3" data-testid="signup-unavailable">
              <Banner tone="warning">{SIGNUP_UNAVAILABLE_MESSAGE}</Banner>
              <p className="text-sm text-muted-foreground">
                이미 계정이 있으면{" "}
                <Link className={LINK} href={LOGIN_PATH}>
                  로그인
                </Link>
                해 주세요.
              </p>
            </div>
          ) : (
            <SignupForm onSent={onSent} onUnavailable={onUnavailable} />
          )}
          <p className="text-xs text-muted-foreground">
            가입하면{" "}
            <Link className={LINK} href={TERMS_PATH}>
              이용약관
            </Link>
            과{" "}
            <Link className={LINK} href={PRIVACY_PATH}>
              개인정보 처리방침
            </Link>
            을 확인한 것으로 봅니다. 동의는 회사 정보를 넣을 때 따로 받습니다.
          </p>
        </>
      )}
    </SectionCard>
  </main>
);

// 셀프 가입 — 계정 만들기 → 확인 메일 보낸 화면(이메일은 화면 상태에만, 주소창에 싣지 않는다).
const SignupPage = () => {
  const [sent, setSent] = useState<SentState | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  return <SignupView onReset={() => setSent(null)} onSent={setSent} onUnavailable={() => setUnavailable(true)} sent={sent} unavailable={unavailable} />;
};

export default SignupPage;
